import React, { memo, useCallback, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { colors } from 'src/theme'
import { WarningContainer } from 'src/components/shared/WarningContainer'
import { FundingInvoice } from 'src/interfaces/payment'
import { ResetFundingInvoice, SubmitFundingInvoiceRequest } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { formatCurrencyFromCents } from 'src/utils/transformers'
import { isNonNullableArray } from 'src/utils/baseUtils'
import { GSIcon } from 'src/components/shared/GSIcon'
import { UserProductName } from 'src/constants/user'
import { getAccessInfo } from 'src/selectors/user'
import { ModalWithAnimation as LoaderModal } from '../../shared/modalWithAnimation/ModalWithAnimation'
import processingAnimationData from '../../../assets/animations/processing.json'

interface Props {
  isOpen?: boolean
  onBack(): void
  onClose(): void
  onApproveAndFund(): void
  currentInvoice: FundingInvoice | null
  refreshFundingInvoice(): Promise<void>
  onRefreshPayments: () => void
  onRefreshMCPayments: () => void
}

const InstantFundConfirmModalComponent = ({
  isOpen,
  onBack,
  onClose,
  onApproveAndFund,
  currentInvoice,
  refreshFundingInvoice,
  onRefreshPayments,
  onRefreshMCPayments,
}: Props) => {
  const dispatch = useDispatch()

  const accessInfo = useSelector(getAccessInfo)

  const container = useMemo(() => document.getElementById('root'), [])

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleApproveAndFund = useCallback(async () => {
    if (!currentInvoice) {
      return null
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        SubmitFundingInvoiceRequest.request({ invoiceId: currentInvoice.id }),
      )

      refreshFundingInvoice()
      onRefreshPayments()

      if (accessInfo?.products?.some((item) => item.product === UserProductName.Subcontractor)) {
        onRefreshMCPayments()
      }

      onApproveAndFund()
      onClose()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [
    onApproveAndFund,
    onClose,
    currentInvoice,
    isLoading,
    onRefreshPayments,
    accessInfo,
    onRefreshMCPayments,
  ])

  const handleResetInvoice = useCallback(async () => {
    if (!currentInvoice) {
      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        ResetFundingInvoice.request({ invoiceId: currentInvoice.id }),
      )

      refreshFundingInvoice()

      onBack()
      onClose()
    } catch {
      handleApiError(
        'We couldn’t cancel your invoice request. Please refresh the page or check your internet connection.',
      )
    } finally {
      setIsLoading(false)
    }
  }, [onBack, onClose, currentInvoice, isLoading])

  const instantRate = useMemo(() => {
    const { gigsafeTotalFeeAmount, amount } = currentInvoice || {}

    if (!amount || !gigsafeTotalFeeAmount) {
      return null
    }

    return parseFloat(((gigsafeTotalFeeAmount / amount) * 100).toFixed(2))
  }, [currentInvoice])

  const totalRepayment = useMemo(() => {
    if (!currentInvoice?.amount || !currentInvoice?.gigsafeTotalFeeAmount) {
      return null
    }

    return currentInvoice.amount + currentInvoice.gigsafeTotalFeeAmount
  }, [currentInvoice?.amount, currentInvoice?.gigsafeTotalFeeAmount])

  if (isOpen && !isNonNullableArray(currentInvoice?.invoiceIds)) {
    return (
      <LoaderModal
        isOpen={true}
        onCancel={handleResetInvoice}
        title={'Analyzing Your Payments'}
        description={
          'We’re reviewing and organizing your payment data — this may take up to 15 seconds.'
        }
        animationData={processingAnimationData}
      />
    )
  }

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
    >
      <div css={tw`bg-white rounded-lg overflow-hidden w-[474px]`}>
        <ModalHeader css={tw`h-[46px]`} title="Fund payments" showCloseButton={false} />
        <div css={tw`p-4`}>
          <GSIcon iconName="gs_zap" css={tw`mb-2 w-5 h-5 self-center`} />
          <Typography variant="h4" css={tw`mb-1`}>
            Instant
          </Typography>
          <Typography variant="body2" color={colors.SHADES_GREY_400}>
            If you accept the{' '}
            <a css={tw`no-underline`} href="https://www.gigsafe.com/terms" target="_blank">
              <Typography
                css={tw`cursor-pointer inline-block hover:text-[rgba(95, 0, 191, 1)]`}
                variant="body2"
                color={colors.PRIMARY_PURPLE_500}
              >
                Terms & Conditions
              </Typography>
            </a>
            , contractor payments will be immediately funded by a third-party financing firm and you
            will be charged a fee for this service.
          </Typography>
          <WarningContainer
            className="mt-4 mb-6"
            title="Important"
            subtitle="Same-day bank transfers must be submitted by 4:45 p.m. ET on business days; otherwise, they will be processed the next business day."
          />
          <div css={tw`grid grid-cols-2 gap-6 w-3/4`}>
            <div>
              <Typography css={tw`mb-1`} variant="h2">
                {currentInvoice?.amount && formatCurrencyFromCents(currentInvoice.amount)}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                Amount to fund
              </Typography>
            </div>
            <div>
              <Typography css={tw`mb-1`} variant="h2">
                {currentInvoice?.earningsCount}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                Payments
              </Typography>
            </div>
            <div>
              <Typography css={tw`mb-1`} variant="h2">
                {!!totalRepayment && formatCurrencyFromCents(totalRepayment)}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                Total repayment
              </Typography>
            </div>
            <div>
              <Typography css={tw`mb-1`} variant="h2">
                {instantRate}%
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                Instant rate
              </Typography>
            </div>
            <div>
              <Typography css={tw`mb-1 whitespace-nowrap`} variant="h2">
                {dayjs(currentInvoice?.loanDueDate).format('MMM D, YYYY')}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                Repayment due
              </Typography>
            </div>
          </div>
          <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-2 mt-6`}>
            To proceed, simply click "Approve and fund". An autogenerate invoice will be available
            for your records. The total repayment amount will be debited from your account on file
            on the repayment date.
          </Typography>
        </div>
        <div
          css={tw`px-4 py-3 flex flex-row justify-between items-center border-0 border-t border-solid border-[#EDEDED]`}
        >
          <Button size="small" color="grey" onClick={handleResetInvoice} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            size="small"
            color="light"
            onClick={handleApproveAndFund}
            disabled={isLoading}
            loading={!isNonNullableArray(currentInvoice?.invoiceIds)}
          >
            Approve and Fund
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const InstantFundConfirmModal = memo(InstantFundConfirmModalComponent)
